import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { CardGridSkeleton, Skeleton } from "../components/ui/Skeleton";
import { aiApi } from "../api/aiApi";
import { clientsApi } from "../api/clientsApi";
import { disputesApi } from "../api/disputesApi";
import { Client, CreditAnalysis, DisputeItem } from "../types";

interface AnalysisRow {
  client: Client;
  analysis: CreditAnalysis | null;
  loading: boolean;
}

function priorityClass(priority: string) {
  switch (priority.toLowerCase()) {
    case "high":
      return "bg-red-500/20 text-red-300 border-red-500/40";
    case "medium":
      return "bg-amber-500/20 text-amber-300 border-amber-500/40";
    case "low":
      return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
    default:
      return "bg-purple-500/20 text-purple-300 border-purple-500/40";
  }
}

const AiAnalysisPage: React.FC = () => {
  const [rows, setRows] = useState<AnalysisRow[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [clientDisputes, setClientDisputes] = useState<DisputeItem[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    const load = async () => {
      setPageLoading(true);
      setPageError("");
      try {
        const clients = await clientsApi.getAll();
        const analyses = await Promise.all(
          clients.map(async (client) => {
            const analysis = await aiApi.getLatestAnalysis(client.id).catch(() => null);
            return {
              client,
              analysis,
              loading: false,
            } as AnalysisRow;
          })
        );
        setRows(analyses);
        if (analyses.length > 0) {
          setSelectedClientId(analyses[0].client.id);
        }
      } catch {
        setPageError("Failed to load AI analysis data.");
      } finally {
        setPageLoading(false);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    const loadDisputes = async () => {
      if (!selectedClientId) {
        setClientDisputes([]);
        return;
      }

      const data = await disputesApi.getAllForClient(selectedClientId).catch(() => []);
      setClientDisputes(data);
    };
    void loadDisputes();
  }, [selectedClientId]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.client.id === selectedClientId) ?? null,
    [rows, selectedClientId]
  );

  const rankedDisputes = useMemo(
    () => [...clientDisputes].sort((a, b) => b.priorityRank - a.priorityRank).slice(0, 5),
    [clientDisputes]
  );

  const runAnalysis = async (clientId: string) => {
    setRows((prev) => prev.map((r) => (r.client.id === clientId ? { ...r, loading: true } : r)));

    try {
      await aiApi.analyzeCreditReport({
        clientId,
        creditReportJson: JSON.stringify({
          generatedAt: new Date().toISOString(),
          note: "Triggered from AI Analysis page",
        }),
      });

      const latest = await aiApi.getLatestAnalysis(clientId).catch(() => null);
      setRows((prev) =>
        prev.map((r) =>
          r.client.id === clientId
            ? {
                ...r,
                analysis: latest,
                loading: false,
              }
            : r
        )
      );
    } catch {
      setRows((prev) => prev.map((r) => (r.client.id === clientId ? { ...r, loading: false } : r)));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">AI Analysis</h1>
          <p className="text-sm text-purple-300/80">Run and review IntelliWarrior AI credit strategy insights.</p>
        </div>

        {pageLoading && (
          <div className="space-y-4 py-4" aria-busy="true" aria-label="Loading analyses">
            <Skeleton className="h-8 w-72" />
            <CardGridSkeleton cards={4} />
          </div>
        )}
        {!pageLoading && pageError && <p className="text-sm text-red-300">{pageError}</p>}

        {!pageLoading && !pageError && (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <section className="rounded-xl border border-purple-800/50 bg-slate-900 p-5">
              <h2 className="mb-4 text-lg font-semibold text-white">Client Analysis Queue</h2>
              <div className="space-y-3">
                {rows.map((row) => {
                  const analysis = row.analysis;
                  const low = analysis?.estimatedScoreImprovementLow ?? 0;
                  const high = analysis?.estimatedScoreImprovementHigh ?? 0;
                  return (
                    <div
                      key={row.client.id}
                      className={`rounded-lg border p-4 transition ${
                        selectedClientId === row.client.id
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-slate-800 bg-slate-950"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button
                          onClick={() => setSelectedClientId(row.client.id)}
                          className="text-left"
                        >
                          <p className="font-medium text-white">{`${row.client.firstName} ${row.client.lastName}`}</p>
                          <p className="text-xs text-slate-400">{row.client.email}</p>
                        </button>
                        <button
                          onClick={() => runAnalysis(row.client.id)}
                          disabled={row.loading}
                          className="rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {row.loading ? "Analyzing..." : "Run Analysis"}
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <span
                          className={`rounded-full border px-2 py-1 ${
                            analysis ? priorityClass(analysis.priorityLevel) : "border-slate-700 text-slate-400"
                          }`}
                        >
                          {analysis?.priorityLevel || "Not analyzed"}
                        </span>
                        <span className="rounded-full border border-slate-700 px-2 py-1 text-slate-300">
                          Est. improvement: +{low} to +{high}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="rounded-xl border border-purple-800/50 bg-slate-900 p-5">
              <h2 className="mb-4 text-lg font-semibold text-white">AI Recommendations</h2>
              {!selectedRow ? (
                <p className="text-sm text-slate-400">Select a client to view analysis details.</p>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="text-sm text-slate-400">Action Plan</p>
                    <p className="mt-1 font-medium text-white">
                      {selectedRow.analysis?.actionPlanHeadline || "No analysis generated yet."}
                    </p>
                  </div>

                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="mb-2 text-sm text-slate-400">Top Disputes Recommended by Impact</p>
                    {rankedDisputes.length === 0 ? (
                      <p className="text-sm text-slate-500">No dispute recommendations available.</p>
                    ) : (
                      <ul className="space-y-2 text-sm text-slate-200">
                        {rankedDisputes.map((d) => (
                          <li key={d.id} className="rounded-md border border-slate-800 px-3 py-2">
                            {d.creditor} - impact rank #{d.priorityRank}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <p className="mb-2 text-sm text-slate-400">Action Plan Checklist</p>
                    {selectedRow.analysis?.actionPlanStepsJson ? (
                      <ul className="space-y-2 text-sm text-slate-200">
                        {selectedRow.analysis.actionPlanStepsJson
                          .replace("[", "")
                          .replace("]", "")
                          .split(",")
                          .map((step, i) => step.trim())
                          .filter(Boolean)
                          .map((step, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="mt-0.5 text-purple-300">☐</span>
                              <span>{step.replace(/"/g, "")}</span>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-slate-500">Run analysis to generate checklist steps.</p>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AiAnalysisPage;
